import React, { useState, useMemo, Suspense, useEffect } from 'react';
import {
  Box,
  Drawer,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Tooltip,
  ListItemText,
  ListItemIcon,
  ListItem,
  IconButton,
  Skeleton,
} from '@mui/material';
import AddInitiativeForm from './AddInitiativeForm';
import { DateTime } from 'luxon';
import { useTheme } from '@mui/material/styles';
import { lighten, darken } from '@mui/material/styles';

import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import Co2RoundedIcon from '@mui/icons-material/Co2Rounded';
import WbCloudyRoundedIcon from '@mui/icons-material/WbCloudyRounded';
import HelpRoundedIcon from '@mui/icons-material/HelpRounded';

import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';

import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';

import baselineLight from '../../../../assets/baseline_light.png';
import baselineDark from '../../../../assets/baseline_dark.png';
import initiativeLight from '../../../../assets/initiative_light.png';
import initiativeDark from '../../../../assets/initiative_dark.png';
import comparisonLight from '../../../../assets/post_init_light.png';
import comparisonDark from '../../../../assets/post_init_dark.png';
import projectedLight from '../../../../assets/projected_light.png';
import projectedDark from '../../../../assets/projected_dark.png';
import averageLight from '../../../../assets/average_light.png';
import averageDark from '../../../../assets/average_dark.png';

import { useTranslation } from 'react-i18next';

import ArrowDropUpRoundedIcon from '@mui/icons-material/ArrowDropUpRounded';
import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';

import InfoRoundedIcon from '@mui/icons-material/InfoRounded';

import { useGetEnergySavingsQuery } from '../../../../api/energy-analysis/calculateEnergySavingsApiSlice';

import {
  usePostEnergyInitiativeMutation,
  useDeleteEnergyInitiativeMutation,
  usePutEnergyInitiativeMutation,
} from '../../../../api/recalcV2/endpoints/energyInitiativesApiSlice';

import { useDispatch } from 'react-redux';
import { showSnackbar } from '../../../../redux/snackbarSlice';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CompletedInitiativeDataGrid from '@/features/energyxplorer/components/initiatives/CompletedInitiativeDataGrid';
import ActiveInitiativeDataGrid from '@/features/energyxplorer/components/initiatives/ActiveInitiativeDataGrid';
import InitiatedInitiativeDataGrid from '@/features/energyxplorer/components/initiatives/InitiatedInitiativeDataGrid';
import InitiativeChart from '@/features/energyxplorer/components/initiatives/InitiativeChart';

function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && (
        <Box sx={{ py: 2 }}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

export const formatTimestampForAPI = (date) => {
  return DateTime.fromISO(date, { zone: 'utc' }) // Ensure UTC interpretation
    .startOf('hour') // Round down to the nearest whole hour
    .toUTC() // Convert explicitly to UTC
    .toFormat("yyyy-MM-dd'T'HH:mm:ssZZ"); // Format as required
};

const InitiativesTab = ({ selectedEnergyGroup }) => {
  const {
    data: energySavingsData,
    isLoading: isLoadingEnergySavingsData,
    isFetching: isFetchingEnergySavingsData,
  } = useGetEnergySavingsQuery(
    {
      energyGroupId: selectedEnergyGroup?.energyGroupId,
      accountId: selectedEnergyGroup?.accountId,
    },
    {
      skip: !selectedEnergyGroup?.energyGroupId,
    },
  );

  const { t } = useTranslation('energyxplorer-initiatives-tab');

  const steps = [
    {
      label: t('common.baseline_period'),
      img: (
        <img
          width="100%"
          style={{ border: '1px solid rgb(235, 235, 235)', borderRadius: 4 }}
          src={baselineLight}
          alt="Baselineperioden"
        />
      ),
      imgDark: (
        <img
          width="100%"
          style={{ border: '1px solid rgb(36, 36, 36)', borderRadius: 4 }}
          src={baselineDark}
          alt="Baselineperioden"
        />
      ),
    },
    {
      label: t('common.initiative'),
      img: (
        <img
          width="100%"
          style={{ border: '1px solid rgb(235, 235, 235)', borderRadius: 4 }}
          src={initiativeLight}
          alt="Initiativet"
        />
      ),
      imgDark: (
        <img
          width="100%"
          style={{ border: '1px solid rgb(36, 36, 36)', borderRadius: 4 }}
          src={initiativeDark}
          alt="Initiativet"
        />
      ),
    },
    {
      label: t('common.comparison_period'),
      img: (
        <img
          width="100%"
          style={{ border: '1px solid rgb(235, 235, 235)', borderRadius: 4 }}
          src={comparisonLight}
          alt="Sammenligning"
        />
      ),
      imgDark: (
        <img
          width="100%"
          style={{ border: '1px solid rgb(36, 36, 36)', borderRadius: 4 }}
          src={comparisonDark}
          alt="Sammenligning"
        />
      ),
    },
    {
      label: t('common.projected'),
      img: (
        <img
          width="100%"
          style={{ border: '1px solid rgb(235, 235, 235)', borderRadius: 4 }}
          src={projectedLight}
          alt="Projekteret"
        />
      ),
      imgDark: (
        <img
          width="100%"
          style={{ border: '1px solid rgb(36, 36, 36)', borderRadius: 4 }}
          src={projectedDark}
          alt="Projekteret"
        />
      ),
    },
    {
      label: t('common.average'),
      img: (
        <img
          width="100%"
          style={{ border: '1px solid rgb(235, 235, 235)', borderRadius: 4 }}
          src={averageLight}
          alt="Gennemsnit"
        />
      ),
      imgDark: (
        <img
          width="100%"
          style={{ border: '1px solid rgb(36, 36, 36)', borderRadius: 4 }}
          src={averageDark}
          alt="Gennemsnit"
        />
      ),
    },
  ];

  const getStepDescription = (step) => {
    return t(`step_descriptions.${step}`);
  };

  const [selectedInitiative, setSelectedInitiative] = useState(null);
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);

  const [postEnergyInitiative] = usePostEnergyInitiativeMutation();
  const [deleteEnergyInitiative] = useDeleteEnergyInitiativeMutation();
  const [putEnergyInitiative] = usePutEnergyInitiativeMutation();

  const [value, setValue] = useState(1); // Tab value

  const [activeStep, setActiveStep] = React.useState(0);

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleReset = () => {
    setActiveStep(0);
  };

  const [formMode, setFormMode] = useState('add'); // Tracks if the form is in 'add' or 'edit' mode
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [selectedInitiativeForMenu, setSelectedInitiativeForMenu] =
    useState(null); // Store the selected initiative for the menu
  const [initiativeDrawerOpen, setInitiativeDrawerOpen] = useState(false); // Drawer state
  const [openInitiativePlotDialog, setOpenInitiativePlotDialog] =
    useState(false);

  const [projectedEnergyAnalysisData, setProjectedEnergyAnalysisData] =
    useState(null);

  const theme = useTheme();

  const dispatch = useDispatch();

  const handleAddInitiative = () => {
    setSelectedInitiative(null); // Clear any selected initiative
    setInitiativeDrawerOpen(true);
  };

  const handleEditInitiative = (initiative) => {
    if (!initiative) return;
    if (selectedInitiative?.id !== initiative?.id) {
      setSelectedInitiative(initiative);
      setFormMode('edit');
      setInitiativeDrawerOpen(true);
    } else {
      setInitiativeDrawerOpen(!initiativeDrawerOpen);
    }
  };

  const handleDeleteInitiative = async () => {
    if (!selectedInitiative) return;

    try {
      await deleteEnergyInitiative({
        accountId: selectedEnergyGroup.accountId,
        energyInitiativeId: selectedInitiative.id,
      });

      dispatch(
        showSnackbar({ message: 'Initiativ fjernet', severity: 'success' }),
      );
      setInitiativeDrawerOpen(false);
    } catch (error) {
      dispatch(
        showSnackbar({
          message: 'Der opstod en fejl under fjernelse af initiativ',
          severity: 'error',
        }),
      );
    }
  };

  const handleMenuClick = (event, initiative) => {
    setAnchorEl(event.currentTarget);
    setSelectedInitiativeForMenu(initiative); // Store the selected initiative for later use
  };

  const handleCloseDrawer = () => {
    setInitiativeDrawerOpen(false);
  };

  const handleSelectInitiative = (initiative) => {
    setSelectedInitiative(initiative);
    setOpenInitiativePlotDialog(true);
  };

  const handleCloseInitiativePlotDialog = () => {
    setOpenInitiativePlotDialog(false);
    setSelectedInitiative(null);
  };

  const formatNumber = (number) => {
    return new Intl.NumberFormat('da-DK').format(number);
  };

  const enrichedInitiatives = useMemo(() => {
    const initiatives = selectedEnergyGroup?.energyInitiatives || [];
    const savings = energySavingsData?.InitiativeEnergySavings || [];

    // Create a lookup map from InitiativeEnergySavings
    const savingsMap = new Map(savings.map((s) => [s.Id, s]));

    // Merge matching savings data into each initiative
    return initiatives.map((initiative) => {
      const savingsData = savingsMap.get(initiative.energyInitiativeId);
      return {
        ...initiative,
        energySavings: savingsData || null,
      };
    });
  }, [selectedEnergyGroup, energySavingsData?.InitiativeEnergySavings]);

  const stateMap = {
    Created: {
      label: t('common.initiated'),
      display: t('common.data_collection'),
      color: theme.palette.mode === 'dark' ? '#ffb74d' : '#ff9800', // orange-ish
    },
    Active: {
      label: t('common.active'),
      display: t('common.ongoing'),
      color: theme.palette.mode === 'dark' ? '#64b5f6' : '#1976d2', // blue
    },
    Done: {
      label: t('common.done'),
      display: t('common.completed'),
      color: theme.palette.mode === 'dark' ? '#81c784' : '#4caf50', // green
    },
  };

  const completedInitiatives = enrichedInitiatives.filter(
    (initiative) => initiative.state === 'Done',
  );
  const activeInitiatives = enrichedInitiatives.filter(
    (initiative) => initiative.state === 'Active',
  );
  const initInitiatives = enrichedInitiatives.filter(
    (initiative) => initiative.state === 'Created',
  );

  const handleCloseHelpDialog = () => {
    setHelpDialogOpen(false);
    setActiveStep(0);
  };

  const activeInitiativesTotalEnergySavings = activeInitiatives.reduce(
    (acc, initiative) => {
      const energySavings = initiative.energySavings?.AdvancedModelUsed
        ? initiative.energySavings?.AdvancedEnergySavings?.EnergySavingsSummary
            ?.EnergySavings
        : initiative.energySavings?.BasicEnergySavings?.EnergySavingsSummary
            ?.EnergySavings;
      return acc + (typeof energySavings === 'number' ? energySavings : 0);
    },
    0,
  );

  const activeInitiativesTotalCo2eSavings = activeInitiatives?.reduce(
    (acc, initiative) => {
      const co2eSavings = initiative.energySavings?.AdvancedModelUsed
        ? initiative.energySavings?.AdvancedEnergySavings?.CO2eSavingsSummary
            ?.CO2eSavings
        : initiative.energySavings?.BasicEnergySavings?.CO2eSavingsSummary
            ?.CO2eSavings;
      return acc + (typeof co2eSavings === 'number' ? co2eSavings / 1000 : 0); // Convert g to kg
    },
    0,
  );

  const completedInitiativesTotalEnergySavings = completedInitiatives?.reduce(
    (acc, initiative) => {
      const energySavings = initiative.energySavings?.AdvancedModelUsed
        ? initiative.energySavings?.AdvancedEnergySavings?.EnergySavingsSummary
            ?.EnergySavings
        : initiative.energySavings?.BasicEnergySavings?.EnergySavingsSummary
            ?.EnergySavings;
      return acc + (typeof energySavings === 'number' ? energySavings : 0);
    },
    0,
  );

  const completedInitiativesTotalCo2eSavings = completedInitiatives?.reduce(
    (acc, initiative) => {
      const co2eSavings = initiative.energySavings?.AdvancedModelUsed
        ? initiative.energySavings?.AdvancedEnergySavings?.CO2eSavingsSummary
            ?.CO2eSavings
        : initiative.energySavings?.BasicEnergySavings?.CO2eSavingsSummary
            ?.CO2eSavings;
      return acc + (typeof co2eSavings === 'number' ? co2eSavings / 1000 : 0); // Convert g to kg
    },
    0,
  );

  return (
    <>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <Box display="flex" alignItems="center">
          {[
            {
              label: `${t('common.done')}: ${completedInitiatives?.length}`, //change to completed
              count: completedInitiatives?.length, //change to completed
              color: stateMap['Done'].color,
              index: 0,
            },
            {
              label: `${t('common.ongoing')}: ${activeInitiatives?.length}`,
              count: activeInitiatives?.length,
              color: stateMap['Active'].color,
              index: 1,
            },
            {
              label: `${t('common.data_collection')}: ${initInitiatives?.length}`,
              count: initInitiatives?.length,
              color: stateMap['Created'].color,
              index: 2,
            },
          ].map(({ label, count, color, index }, i) => (
            <Box
              key={index}
              sx={{
                position: 'relative',
                ml: i === 0 ? 0 : 1,
              }}
            >
              <Button
                variant="contained"
                disabled={count === 0}
                onClick={() => setValue(index)}
                disableElevation
                sx={{
                  color: 'hsl(0, 0%, 100%)',
                  backgroundColor: color,
                  '&:hover': {
                    backgroundColor: darken(color, 0.1),
                  },
                }}
              >
                {label}
              </Button>
              {value === index && enrichedInitiatives.length !== 0 && (
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: -4,
                    left: 0,
                    right: 0,
                    height: 2,
                    backgroundColor: lighten(color, 0.2),
                    borderRadius: 1,
                  }}
                />
              )}
            </Box>
          ))}
          <IconButton onClick={() => setHelpDialogOpen(true)} sx={{ ml: 1 }}>
            <HelpRoundedIcon
              sx={{
                color: (theme) =>
                  theme.palette.mode === 'light' ? 'lightgray' : 'gray',
              }}
            />
          </IconButton>
        </Box>
        <Button
          variant="contained"
          startIcon={
            <AddRoundedIcon
              sx={{ transition: 'margin-right 0.1s ease-in-out' }}
            />
          }
          onClick={handleAddInitiative}
        >
          {t('button_labels.add_initiative')}
        </Button>
      </Box>
      {enrichedInitiatives.length !== 0 && (
        <Grid container spacing={2}>
          {value === 0 &&
            (isLoadingEnergySavingsData || isFetchingEnergySavingsData ? (
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Skeleton
                  variant="rectangular"
                  width="50%"
                  sx={{ mr: 1 }}
                  height={118}
                />
                <Skeleton
                  variant="rectangular"
                  width="50%"
                  sx={{ mr: 1 }}
                  height={118}
                />
              </Box>
            ) : (
              <Grid item xs>
                <Card variant="outlined">
                  <CardContent>
                    <>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', mb: 2 }}
                      >
                        <Typography
                          variant="body1"
                          color="textSecondary"
                          fontWeight="bold"
                        >
                          Samlet CO2e reduktion
                        </Typography>
                        <Tooltip
                          title="Her ses den samlede CO2e reduktion på tværs af de afsluttede initiativer. "
                          placement="right"
                        >
                          <InfoRoundedIcon
                            fontSize="small"
                            sx={{
                              color: (theme) =>
                                theme.palette.mode === 'light'
                                  ? 'lightgray'
                                  : 'gray',
                              ml: 1,
                            }}
                          />
                        </Tooltip>
                      </Box>
                      <ListItem sx={{ display: 'flex', alignItems: 'center' }}>
                        <ListItemIcon>
                          <WbCloudyRoundedIcon
                            fontSize="large"
                            sx={{ mr: 1, color: 'gray' }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              {completedInitiativesTotalCo2eSavings > 0 ? (
                                <ArrowDropDownRoundedIcon
                                  fontSize="large"
                                  color="success"
                                  sx={{ mb: 2 }}
                                />
                              ) : (
                                <ArrowDropUpRoundedIcon
                                  fontSize="large"
                                  color="error"
                                  sx={{ mb: 2 }}
                                />
                              )}
                              <Typography
                                variant="h4"
                                color="textPrimary"
                                fontWeight="bold"
                                gutterBottom
                              >
                                {completedInitiativesTotalCo2eSavings
                                  ? `${formatNumber(completedInitiativesTotalCo2eSavings.toFixed(0))} kg CO2e`
                                  : '0 kg CO2e'}
                              </Typography>
                            </Box>
                          }
                          secondary={
                            <Typography variant="body2" color="textSecondary">
                              {`Samlet CO2e reduktion for ${completedInitiatives.length} afsluttede initiativer`}
                            </Typography>
                          }
                        />
                      </ListItem>
                    </>
                  </CardContent>
                </Card>
              </Grid>
            ))}

          {value === 1 &&
            (isLoadingEnergySavingsData || isFetchingEnergySavingsData ? (
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Skeleton
                  variant="rectangular"
                  width="50%"
                  sx={{ mr: 1 }}
                  height={118}
                />
                <Skeleton
                  variant="rectangular"
                  width="50%"
                  sx={{ mr: 1 }}
                  height={118}
                />
              </Box>
            ) : (
              <Grid item xs>
                <Card variant="outlined">
                  <CardContent>
                    <>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', mb: 2 }}
                      >
                        <Typography
                          variant="body1"
                          color="textSecondary"
                          fontWeight="bold"
                        >
                          {t('common.total_co2_reduction')}
                        </Typography>
                        <Tooltip
                          title={t('common.total_co2_reduction_description')}
                          placement="right"
                        >
                          <InfoRoundedIcon
                            fontSize="small"
                            sx={{
                              color: (theme) =>
                                theme.palette.mode === 'light'
                                  ? 'lightgray'
                                  : 'gray',
                              ml: 1,
                            }}
                          />
                        </Tooltip>
                      </Box>
                      <ListItem sx={{ display: 'flex', alignItems: 'center' }}>
                        <ListItemIcon>
                          <WbCloudyRoundedIcon
                            fontSize="large"
                            sx={{ mr: 1, color: 'gray' }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              {activeInitiativesTotalCo2eSavings > 0 ? (
                                <ArrowDropDownRoundedIcon
                                  fontSize="large"
                                  color="success"
                                  sx={{ mb: 2 }}
                                />
                              ) : (
                                <ArrowDropUpRoundedIcon
                                  fontSize="large"
                                  color="error"
                                  sx={{ mb: 2 }}
                                />
                              )}
                              <Typography
                                variant="h4"
                                color="textPrimary"
                                fontWeight="bold"
                                gutterBottom
                              >
                                {activeInitiativesTotalCo2eSavings
                                  ? `${formatNumber(activeInitiativesTotalCo2eSavings.toFixed(0))} kg CO2e`
                                  : '0 kg CO2e'}
                              </Typography>
                            </Box>
                          }
                          secondary={
                            <Typography variant="body2" color="textSecondary">
                              {`${t('common.total_co2_reduction_for')} ${activeInitiatives.length} ${t('common.ongoing_initiatives')}`}
                            </Typography>
                          }
                        />
                      </ListItem>
                    </>
                  </CardContent>
                </Card>
              </Grid>
            ))}

          {value === 0 &&
            (isLoadingEnergySavingsData || isFetchingEnergySavingsData ? (
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Skeleton
                  variant="rectangular"
                  width="50%"
                  sx={{ mr: 1 }}
                  height={118}
                />
                <Skeleton
                  variant="rectangular"
                  width="50%"
                  sx={{ mr: 1 }}
                  height={118}
                />
              </Box>
            ) : (
              <Grid item xs>
                <Card variant="outlined">
                  <CardContent>
                    <>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', mb: 2 }}
                      >
                        <Typography
                          variant="body1"
                          color="textSecondary"
                          fontWeight="bold"
                        >
                          {t('common.total_energy_savings')}
                        </Typography>
                        <Tooltip
                          title={t('common.total_energy_savings_description')}
                          placement="right"
                        >
                          <InfoRoundedIcon
                            fontSize="small"
                            sx={{
                              color: (theme) =>
                                theme.palette.mode === 'light'
                                  ? 'lightgray'
                                  : 'gray',
                              ml: 1,
                            }}
                          />
                        </Tooltip>
                      </Box>

                      <ListItem sx={{ display: 'flex', alignItems: 'center' }}>
                        <ListItemIcon>
                          <BoltRoundedIcon
                            fontSize="large"
                            sx={{ mr: 1, color: 'gray' }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              {completedInitiativesTotalEnergySavings > 0 ? (
                                <ArrowDropDownRoundedIcon
                                  fontSize="large"
                                  color="success"
                                  sx={{ mb: 2 }}
                                />
                              ) : (
                                <ArrowDropUpRoundedIcon
                                  fontSize="large"
                                  color="error"
                                  sx={{ mb: 2 }}
                                />
                              )}
                              <Typography
                                variant="h4"
                                color="textPrimary"
                                fontWeight="bold"
                                gutterBottom
                              >
                                {completedInitiativesTotalEnergySavings
                                  ? `${formatNumber(completedInitiativesTotalEnergySavings.toFixed(0))} kWh`
                                  : '0 kWh'}
                              </Typography>
                            </Box>
                          }
                          secondary={
                            <Typography variant="body2" color="textSecondary">
                              {`${t('common.total_energy_savings_for')} ${completedInitiativesTotalEnergySavings.length} ${t('common.active_initiatives')}`}
                            </Typography>
                          }
                        />
                      </ListItem>
                    </>
                  </CardContent>
                </Card>
              </Grid>
            ))}

          {value === 1 &&
            (isLoadingEnergySavingsData || isFetchingEnergySavingsData ? (
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Skeleton
                  variant="rectangular"
                  width="50%"
                  sx={{ mr: 1 }}
                  height={118}
                />
                <Skeleton
                  variant="rectangular"
                  width="50%"
                  sx={{ mr: 1 }}
                  height={118}
                />
              </Box>
            ) : (
              <Grid item xs>
                <Card variant="outlined">
                  <CardContent>
                    <>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', mb: 2 }}
                      >
                        <Typography
                          variant="body1"
                          color="textSecondary"
                          fontWeight="bold"
                        >
                          {t('common.total_energy_savings')}
                        </Typography>
                        <Tooltip
                          title={t('common.total_energy_savings_description')}
                          placement="right"
                        >
                          <InfoRoundedIcon
                            fontSize="small"
                            sx={{
                              color: (theme) =>
                                theme.palette.mode === 'light'
                                  ? 'lightgray'
                                  : 'gray',
                              ml: 1,
                            }}
                          />
                        </Tooltip>
                      </Box>

                      <ListItem sx={{ display: 'flex', alignItems: 'center' }}>
                        <ListItemIcon>
                          <BoltRoundedIcon
                            fontSize="large"
                            sx={{ mr: 1, color: 'gray' }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              {activeInitiativesTotalEnergySavings > 0 ? (
                                <ArrowDropDownRoundedIcon
                                  fontSize="large"
                                  color="success"
                                  sx={{ mb: 2 }}
                                />
                              ) : (
                                <ArrowDropUpRoundedIcon
                                  fontSize="large"
                                  color="error"
                                  sx={{ mb: 2 }}
                                />
                              )}
                              <Typography
                                variant="h4"
                                color="textPrimary"
                                fontWeight="bold"
                                gutterBottom
                              >
                                {activeInitiativesTotalEnergySavings
                                  ? `${formatNumber(activeInitiativesTotalEnergySavings.toFixed(0))} kWh`
                                  : '0 kWh'}
                              </Typography>
                            </Box>
                          }
                          secondary={
                            <Typography variant="body2" color="textSecondary">
                              {`${t('common.total_energy_savings_for')} ${activeInitiatives.length} ${t('common.ongoing_initiatives')}`}
                            </Typography>
                          }
                        />
                      </ListItem>
                    </>
                  </CardContent>
                </Card>
              </Grid>
            ))}
        </Grid>
      )}

      <TabPanel value={value} index={0}>
        <Suspense fallback={<div>Loading...</div>}>
          <CompletedInitiativeDataGrid
            value={value}
            isLoading={
              isLoadingEnergySavingsData || isFetchingEnergySavingsData
            }
            enrichedInitiatives={completedInitiatives}
            handleSelectInitiative={handleSelectInitiative}
            handleEditInitiative={handleEditInitiative}
            handleMenuClick={handleMenuClick}
            selectedInitiative={selectedInitiative}
            handleAddInitiative={handleAddInitiative}
            anchorEl={anchorEl}
            setAnchorEl={setAnchorEl}
          />
        </Suspense>
      </TabPanel>

      <TabPanel value={value} index={1}>
        <Suspense fallback={<div>Loading...</div>}>
          <ActiveInitiativeDataGrid
            value={value}
            isLoading={
              isLoadingEnergySavingsData || isFetchingEnergySavingsData
            }
            enrichedInitiatives={activeInitiatives}
            handleSelectInitiative={handleSelectInitiative}
            handleEditInitiative={handleEditInitiative}
            handleMenuClick={handleMenuClick}
            selectedInitiative={selectedInitiative}
            handleAddInitiative={handleAddInitiative}
            anchorEl={anchorEl}
            setAnchorEl={setAnchorEl}
          />
        </Suspense>
      </TabPanel>

      <TabPanel value={value} index={2}>
        <Suspense fallback={<div>Loading...</div>}>
          <InitiatedInitiativeDataGrid
            value={value}
            isLoading={
              isLoadingEnergySavingsData || isFetchingEnergySavingsData
            }
            enrichedInitiatives={initInitiatives}
            handleSelectInitiative={handleSelectInitiative}
            handleEditInitiative={handleEditInitiative}
            handleMenuClick={handleMenuClick}
            selectedInitiative={selectedInitiative}
            handleAddInitiative={handleAddInitiative}
            anchorEl={anchorEl}
            setAnchorEl={setAnchorEl}
          />
        </Suspense>
      </TabPanel>

      <TabPanel value={value} index={3}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <Typography variant="h6" color="textSecondary">
            {t('common.no_initiatves_found')}
          </Typography>
        </Box>
      </TabPanel>

      <Drawer
        anchor="right"
        open={initiativeDrawerOpen}
        onClose={() => setInitiativeDrawerOpen(false)}
      >
        <AddInitiativeForm
          groupId={selectedEnergyGroup?.energyGroupId}
          selectedEnergyGroup={selectedEnergyGroup}
          nodes={selectedEnergyGroup?.energyTreeNodes}
          selectedInitiative={formMode === 'edit' ? selectedInitiative : null}
          addInitiative={postEnergyInitiative}
          deleteInitiative={handleDeleteInitiative}
          updateInitiative={putEnergyInitiative}
          mode={formMode}
          onClose={handleCloseDrawer}
        />
      </Drawer>

      <Dialog
        open={helpDialogOpen}
        onClose={handleCloseHelpDialog}
        fullWidth
        maxWidth="xl"
        // PaperProps={{
        //   elevation: 0,
        //   sx: {
        //     backgroundColor: (theme) =>
        //       theme.palette.mode === 'dark' ? '#000' : '#fff',
        //     border: '1px solid',
        //     borderColor: (theme) => theme.palette.divider,
        //     borderRadius: '8px',
        //   },
        // }}
      >
        <DialogTitle>{t('step_descriptions.title')}</DialogTitle>
        <DialogContent dividers>
          <>
            <Box sx={{ mx: 16, mt: 4 }}>
              <Stepper activeStep={activeStep}>
                {steps.map((step, index) => {
                  const stepProps = {};
                  const labelProps = {};
                  return (
                    <Step key={step.label} {...stepProps}>
                      <StepLabel
                        {...labelProps}
                        StepIconProps={{
                          sx: {
                            '&.Mui-completed': {
                              color: 'success.main',
                            },
                            '&.Mui-active': {
                              color: 'primary.main',
                              scale: 1.2,
                              transition: 'transform 0.3s ease-in-out',
                            },
                          },
                        }}
                      >
                        {step.label}
                      </StepLabel>
                    </Step>
                  );
                })}
              </Stepper>
              <Box
                sx={{
                  display: 'flex',
                  align: 'center',
                  flexDirection: 'column',
                  pt: 2,
                }}
              >
                {activeStep === steps.length ? (
                  <React.Fragment>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        mt: 2,
                        mb: 1,
                      }}
                    >
                      <Typography>{t('step_descriptions.default')}</Typography>
                    </Box>
                    <Box
                      sx={{ display: 'flex', justifyContent: 'center', pt: 2 }}
                    >
                      <Button onClick={handleReset}>
                        {t('step_descriptions.reset')}
                      </Button>
                    </Box>
                  </React.Fragment>
                ) : (
                  <>
                    <Box
                      sx={{ display: 'flex', flexDirection: 'column', mt: 2 }}
                    >
                      {theme.palette.mode === 'dark'
                        ? steps[activeStep].imgDark
                        : steps[activeStep].img}
                      <Typography
                        sx={{ mt: 2, mb: 1 }}
                        variant="body1"
                        color="textSecondary"
                      >
                        {getStepDescription(activeStep)}
                      </Typography>
                    </Box>
                    <Box
                      sx={{ display: 'flex', justifyContent: 'center', pt: 2 }}
                    >
                      <IconButton
                        color="inherit"
                        disabled={activeStep === 0}
                        onClick={handleBack}
                        sx={{ mr: 1 }}
                      >
                        <ArrowBackRoundedIcon />
                      </IconButton>

                      <IconButton
                        color="inherit"
                        disabled={activeStep === steps.length}
                        onClick={handleNext}
                        sx={{ ml: 1 }}
                      >
                        <ArrowForwardRoundedIcon />
                      </IconButton>
                    </Box>
                  </>
                )}
              </Box>
            </Box>
          </>
        </DialogContent>
      </Dialog>

      <Dialog
        open={openInitiativePlotDialog}
        onClose={handleCloseInitiativePlotDialog}
        fullWidth
        maxWidth="xl"
      >
        <DialogContent dividers>
          <Suspense
            fallback={
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
              </Box>
            }
          >
            <InitiativeChart
              isLoading={
                isLoadingEnergySavingsData || isFetchingEnergySavingsData
              }
              selectedInitiative={selectedInitiative}
              updateInitiative={putEnergyInitiative}
              projectedEnergyAnalysisData={projectedEnergyAnalysisData}
            />
          </Suspense>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default InitiativesTab;
